"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldAlert } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const auditItems = [{
  index: "01",
  title: "Bull & Motion Base",
  description: "Confirm whether the main mechanical assembly is included."
}, {
  index: "02",
  title: "Controller",
  description: "Check whether the operator controller shown or described comes with the machine."
}, {
  index: "03",
  title: "Safety Arena & Blower",
  description: "Confirm whether the inflatable safety area and blower are part of the advertised package."
}, {
  index: "04",
  title: "Cables & Accessories",
  description: "Check for cables, remotes, covers, documentation and other equipment listed with the product."
}];
export default function Included() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whatsincludedintheprice" data-nav="light" className="relative bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 md:mb-16 border-b border-border pb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="inline-flex items-center gap-2">
              <span className="inline-block size-2 bg-primary" />
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                PACKAGE BREAKDOWN // EQUIPMENT AUDIT
              </p>
            </div>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground max-w-4xl">
            What Is Included in the Mechanical Bull Price?
          </h2>

          <p className="mt-4 font-sans text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
            The advertised price only becomes meaningful once you understand what comes with the machine.
          </p>
        </motion.div>

        {/* Industrial Specification Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          {auditItems.map((item, index) => <motion.div key={item.index} data-index={index} variants={itemVariants} className="h-full">
              <Card className="h-full rounded-none border border-border bg-card shadow-none transition-[border-color,background-color] duration-200 hover:border-foreground py-0">
                <CardHeader className="p-5 md:p-6 border-b border-border space-y-3">
                  <div>
                    <span className="font-mono text-2xl md:text-3xl font-bold text-foreground/80 tracking-tight">
                      {item.index}
                    </span>
                  </div>
                  <CardTitle className="font-serif text-xl md:text-2xl font-bold uppercase tracking-tight text-card-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-6 flex flex-col justify-between flex-1 gap-4">
                  <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                  <div className="pt-2 border-t border-border/60 flex items-center gap-2 text-xs font-mono text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>MANDATORY VERIFICATION</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Warning Plate Alert Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }}>
          <Alert className="rounded-none border border-border bg-card p-4 md:p-6 shadow-none border-l-4 border-l-primary flex items-start gap-3 sm:gap-4">
            <ShieldAlert className="size-5 md:size-6 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <AlertTitle className="font-mono text-xs uppercase tracking-wider text-foreground font-semibold flex items-center gap-2">
                <span>BUYER NOTICE</span>
                <span className="text-muted-foreground">//</span>
                <span className="text-muted-foreground">INVENTORY POLICY</span>
              </AlertTitle>
              <AlertDescription className="font-sans text-sm md:text-base text-foreground font-medium">
                If something is not shown as included, do not assume it comes with the advertised price.
              </AlertDescription>
            </div>
          </Alert>
        </motion.div>
      </div>
    </section>;
}